/* eslint-disable prettier/prettier */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CaretSortIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from 'app/components/ui/button';

import { Input } from 'app/components/ui/input';
import { Toaster } from 'app/components/ui/toaster';
import { Pencil, SearchIcon, Trash } from 'lucide-react';
import { Badge } from 'app/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/hooks/debounce-hook';
import { AlertCourseTagsDelete } from '../delete/delete-confirmation';
import DialogAddProductTag from '../Add';
import DialogUpdateProductTags, { DialogUpdateCourseTags } from '../Edit';
import { useCourseSlice } from '../../slice';
import DialogAddCourseTag from '../Add';

export function DataTable() {
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = React.useState<any>(null);

  const { useLazyGetCourseTagsQuery } = useCourseSlice();
  const [getTags, { data, error, isLoading }] = useLazyGetCourseTagsQuery();

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'tag_id',
      header: 'Tag ID',
      cell: ({ row }) => <div>{row.getValue('tag_id')}</div>,
    },
    {
      accessorKey: 'tag_name',
      header: 'Tag Name',
      cell: ({ row }) => <div>{row.getValue('tag_name')}</div>,
    },
    {
      accessorKey: 'slug',
      header: 'slug',
      cell: ({ row }) => <div>{row.getValue('slug')}</div>,
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-2">
          <DropdownMenu>
            <DropdownMenuTrigger>
              <Button variant={'ghost'}>
                <DotsHorizontalIcon className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRow(row.original);

                  setOpenUpdate(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  setSelectedRow(row.original);

                  setOpenDelete(true);
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const labelsPagination = {
    totalPages: data?.response?.totalPages,
    pageSize: data?.response?.perPage,
  };
  const labels = data?.response?.data || [];
  const dispatch = useDispatch();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [selected, setSelected] = React.useState<number>(0);
  const [rowSelected, setRowSelected] = React.useState<any>([]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: labels,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: labelsPagination?.totalPages,
    rowCount: labelsPagination?.pageSize,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  useEffect(() => {
    getTags({
      page: pagination.pageIndex + 1,
      search: searchDebounce,
    });
  }, [pagination, searchDebounce]);

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h1>Course Tags</h1>
        <div>
          <Button onClick={() => setOpenAdd(true)}>Add Tag</Button>
        </div>
      </div>
      <div className="w-full bg-white  dark:bg-transparent  rounded-[10px] p-3 border border-solid dark:border-white">
        <div className="flex justify-between items-center mb-[15px]">
          <div className="relative">
            <div className="absolute left-[10px] top-[10px]">
              <SearchIcon className="h-4 w-4" />
            </div>
            <Input
              placeholder="Search by name"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-sm pl-[35px] "
            />
          </div>
        </div>
        <div className="rounded-md border">
          <Table>
            <TableHeader className="">
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    return (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map(row => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                  >
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    {isLoading ? 'Loading...' : 'No results found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <div className="flex-1 text-sm text-muted-foreground">
            Page {pagination.pageIndex + 1} of {table.getPageCount()}
          </div>
          <div className="space-x-2">
            <Button
              variant="link"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </Button>
            <Button
              variant="link"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'Prev'}
            </Button>
            <Button
              variant="link"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {'Next'}
            </Button>
            <Button
              variant="link"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </Button>
          </div>
        </div>

        <Toaster />

        <DialogUpdateCourseTags
          isOpen={openUpdate}
          onOpenChange={setOpenUpdate}
          data={selectedRow}
        />

        <DialogAddCourseTag isOpen={openAdd} onClose={setOpenAdd} />

        <AlertCourseTagsDelete
          isOpen={openDelete}
          onOpenChange={setOpenDelete}
          data={selectedRow}
        />
      </div>
    </div>
  );
}
